import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import Header from "../Components/Header";
import RewardLocked, { TrophyIcon } from "../RewardComponent/RewardLocked";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../firebase/config";
import { useGlobal } from "../context/GlobalContext";
import AnimatedCard, { MotionItem } from "../Components/AnimatedCard";

const EASE = [0.22, 1, 0.36, 1];

// Same fade/slide-in as MotionItem, but also staggers its own children -
// used for the rewards grid so each card animates in turn
const gridContainer = {
  hidden: { opacity: 0, y: 16 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: EASE, staggerChildren: 0.08 },
  },
};

const SearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"
    strokeLinecap="round" strokeLinejoin="round" className="h-[18px] w-[18px]" aria-hidden="true">
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.5-3.5" />
  </svg>
);

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
    strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5" aria-hidden="true">
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </svg>
);

const FILTERS = [
  { key: "all", label: "Toutes" },
  { key: "unlocked", label: "Débloquées" },
  { key: "locked", label: "Verrouillées" },
];

const SORTS = [
  { key: "default", label: "Par défaut" },
  { key: "unlocked", label: "Débloquées d'abord" },
  { key: "az", label: "A → Z" },
];

export default function MyRewards({ setIsSidebarOpen, isSidebarOpen }) {
  const { userProgress } = useGlobal();
  const [rewards, setRewards] = useState([]);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("default");

  const activeFilterIndex = FILTERS.findIndex((f) => f.key === filter);

  const isUnlocked = (reward) =>
    !!reward?.unlockAfter?.id &&
    !!userProgress?.completedItems?.includes(reward.unlockAfter.id);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, "rewards"),
      (snapshot) => {
        setRewards(snapshot.docs.map((d) => ({ ...d.data(), id: d.id })));
      },
      (error) => {
        console.error("Error getting rewards data:", error);
      }
    );
    return () => unsubscribe();
  }, []);

  const stats = useMemo(() => {
    const unlocked = rewards.filter(isUnlocked).length;
    const total = rewards.length;
    return {
      total,
      unlocked,
      locked: total - unlocked,
      percent: total ? Math.round((unlocked / total) * 100) : 0,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rewards, userProgress]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = rewards.filter((r) => {
      const matchesSearch =
        !q ||
        r.name?.toLowerCase().includes(q) ||
        r.description?.toLowerCase().includes(q) ||
        r.unlockAfter?.title?.toLowerCase().includes(q);
      const u = isUnlocked(r);
      const matchesFilter = filter === "all" || (filter === "unlocked" ? u : !u);
      return matchesSearch && matchesFilter;
    });

    if (sort === "az") {
      list = [...list].sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", "fr")
      );
    } else if (sort === "unlocked") {
      list = [...list].sort((a, b) => Number(isUnlocked(b)) - Number(isUnlocked(a)));
    }
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rewards, userProgress, search, filter, sort]);

  const counts = { all: stats.total, unlocked: stats.unlocked, locked: stats.locked };

  return (
    <div>
      {/* smooth panel transition (respects reduced motion) */}
      <style>{`
        @keyframes pm-panel-in {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .pm-panel-in { animation: pm-panel-in .35s cubic-bezier(.22,1,.36,1) both; }
        @media (prefers-reduced-motion: reduce) { .pm-panel-in { animation: none; } }
      `}</style>

      <div className="flex flex-col">
        <Header
          PageName={"Récompenses"}
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
        />
      </div>

      <AnimatedCard
        tilt={false}
        effects="light"
        className="bg-bgColor p-[22px] flex flex-col gap-6 rounded-[15px] border border-borderColor"
      >
        {/* Hero / progress */}
        <MotionItem className="relative overflow-hidden rounded-2xl border border-borderColor bg-[linear-gradient(135deg,rgba(10,20,80,0.75)_0%,rgba(6,12,50,0.85)_55%,rgba(5,9,38,0.92)_100%)] p-6">
          <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-[#4268FF]/10 blur-3xl" />
          <div className="relative flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <div className="flex items-start gap-4 text-left">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#113BE1] to-[#4268FF] text-white shadow-lg shadow-blue-500/30">
                <TrophyIcon className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-[20px] font-semibold text-white">
                  Mon niveau de formation
                </h2>
                <p className="mt-1 text-[14px] text-[#A0AEC0]">
                  Retrouvez ici toutes vos récompenses débloquées lors la formation.
                </p>
              </div>
            </div>

            <div className="w-full md:max-w-sm">
              <div className="mb-2 flex items-end justify-between text-sm">
                <span className="text-[#A0AEC0]">
                  <span className="font-semibold text-white">{stats.unlocked}</span> / {stats.total} débloquées
                </span>
                <span className="bg-gradient-to-r from-[#113BE1] to-[#4268FF] bg-clip-text text-lg font-bold text-transparent">
                  {stats.percent}%
                </span>
              </div>
              <div className="h-2.5 overflow-hidden rounded-full bg-primary ring-1 ring-borderColor">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-[#113BE1] via-[#4268FF] to-[#113BE1] shadow-[0_0_12px_rgba(66,104,255,0.6)] transition-[width] duration-700 ease-out"
                  style={{ width: `${stats.percent}%` }}
                />
              </div>
            </div>
          </div>
        </MotionItem>

        {/* Toolbar */}
        <MotionItem className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          {/* Filter tabs */}
          <div
            role="tablist"
            aria-label="Filtrer les récompenses"
            className="relative grid w-full grid-cols-3 rounded-xl border border-borderColor bg-primary p-1 sm:w-auto"
          >
            {/* sliding indicator */}
            <span
              aria-hidden="true"
              className="absolute bottom-1 left-1 top-1 rounded-lg bg-gradient-to-r from-[#113BE1] via-[#4268FF] to-[#113BE1] shadow-lg shadow-[#113BE1]/30 transition-transform duration-500 ease-[cubic-bezier(.22,1,.36,1)]"
              style={{
                width: `calc((100% - 8px) / ${FILTERS.length})`,
                transform: `translateX(${activeFilterIndex * 100}%)`,
              }}
            />
            {FILTERS.map((f) => {
              const active = filter === f.key;
              return (
                <button
                  key={f.key}
                  type="button"
                  role="tab"
                  id={`rewards-tab-${f.key}`}
                  aria-selected={active}
                  aria-controls="rewards-panel"
                  onClick={() => setFilter(f.key)}
                  className={`relative z-10 flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/50 sm:min-w-[130px] sm:px-4 ${
                    active ? "text-white" : "text-[#A0AEC0] hover:text-white"
                  }`}
                >
                  <span className="truncate">{f.label}</span>
                  <span
                    className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold transition-colors duration-300 ${
                      active ? "bg-white/20 text-white" : "bg-white/10 text-gray-400"
                    }`}
                  >
                    {counts[f.key]}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              aria-label="Trier les récompenses"
              className="rounded-xl border border-borderColor bg-primary px-3 py-2.5 text-sm text-gray-200 outline-none transition focus:border-blue-500"
            >
              {SORTS.map((s) => (
                <option key={s.key} value={s.key} className="bg-primary">
                  {s.label}
                </option>
              ))}
            </select>

            <div className="group relative w-full sm:w-72">
              <span className="pointer-events-none absolute inset-y-0 left-2 flex items-center text-gray-400 transition-colors duration-300 group-focus-within:text-blue-400">
                <SearchIcon />
              </span>
              <input
                type="text"
                placeholder="Rechercher une récompense..."
                aria-label="Rechercher une récompense"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="peer w-full border-0 border-b border-borderColor bg-transparent py-2.5 pl-9 pr-9 text-[0.95rem] text-white placeholder-gray-400 shadow-[0_2px_4px_rgba(0,0,0,0.1)] transition-colors focus:outline-none focus:ring-0"
              />
              <span className="pointer-events-none absolute bottom-0 left-0 h-[2px] w-0 bg-gradient-to-r from-[#113BE1] via-[#4268FF] to-[#113BE1] transition-[width] duration-[400ms] peer-focus:w-full" />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  aria-label="Effacer la recherche"
                  className="absolute inset-y-0 right-1 my-auto flex h-7 w-7 items-center justify-center rounded-full text-gray-400 transition hover:bg-white/10 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
                >
                  <CloseIcon />
                </button>
              )}
            </div>
          </div>
        </MotionItem>

        {/* Tab panel: key remounts it on filter change, replaying the transition */}
        <div
          key={filter}
          role="tabpanel"
          id="rewards-panel"
          aria-labelledby={`rewards-tab-${filter}`}
          className="pm-panel-in"
        >
          {/* Grid / empty states */}
          {visible.length === 0 ? (
            <MotionItem className="flex flex-col items-center gap-3 rounded-2xl border border-dashed border-borderColor py-16 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-white/10 text-gray-400">
                {rewards.length === 0 ? <TrophyIcon className="h-7 w-7" /> : <SearchIcon />}
              </div>
              <p className="text-sm text-[#A0AEC0]">
                {rewards.length === 0
                  ? "Aucune récompense disponible pour le moment."
                  : "Aucune récompense ne correspond à votre recherche."}
              </p>
              {(search || filter !== "all") && (
                <button
                  type="button"
                  onClick={() => {
                    setSearch("");
                    setFilter("all");
                  }}
                  className="rounded-lg border border-blue-500/30 bg-blue-500/10 px-4 py-2 text-xs font-semibold text-blue-300 transition hover:bg-blue-500/20"
                >
                  Réinitialiser les filtres
                </button>
              )}
            </MotionItem>
          ) : (
            <motion.div
              variants={gridContainer}
              className="grid grid-cols-1 gap-5 lg:grid-cols-2 xl:grid-cols-3"
            >
              {visible.map((reward) => (
                <RewardLocked
                  key={reward.id}
                  lockedReason={reward?.unlockAfter}
                  title={reward.name}
                  url={reward.url}
                  detail={reward.description}
                  status={isUnlocked(reward) ? "unlocked" : "locked"}
                />
              ))}
            </motion.div>
          )}
        </div>
      </AnimatedCard>
    </div>
  );
}